import type { StatusKind } from './StatusChip';

/**
 * What every status in the platform *means*, in one table.
 *
 * **Why this is central and not per-screen.** Sixteen screens each carried a
 * `STATUS_COLOR` map. Five of them were the contract vocabulary, written out
 * identically five times. Four were the appointment vocabulary — and those four
 * did not agree: the admin portal drew an expired appointment as a warning
 * while the Government Portal drew the same appointment as neutral. The two
 * portals sit side by side in the same job, so one appointment was two colours
 * depending on which tab you were looking at, and neither was wrong on its own
 * terms. That is what a copied map does: it does not stay a copy.
 *
 * A status has one meaning. Deciding it in one place is the only way that stays
 * true, and it puts the decision somewhere a designer can read it end to end
 * rather than reconstructing it from sixteen files.
 *
 * **How the meanings map.** `neutral` is factual and not evaluative — a draft is
 * not a problem. `pending` is the blue: waiting on someone, including "in
 * progress" and "filed", which are waits with work happening. `warning` is
 * "still true but running out" — expiring, suspended, unpaid. `rejected` is the
 * red: ended by a decision. `success` is the working, current state.
 *
 * The tables are string-keyed because `libs/ui` deliberately depends on nothing
 * but `@munifocus/tokens`; pulling in the contracts packages to type them would
 * make the design system depend on the API's shape. Each app's own spec asserts
 * that its unions are covered here, which is where a missing status is caught.
 */
export const statusTone = {
  /** A company's engagement with a municipality. */
  contract: {
    draft: 'neutral',
    pending_council: 'pending',
    active: 'success',
    // Both are "the contract exists but grants nothing right now". Expired is
    // the clock running out, suspended is a decision — the reader needs to act
    // on either, so they read the same.
    suspended: 'warning',
    expired: 'warning',
    terminated: 'rejected',
  },
  /** An officer's appointment to a municipality. */
  appointment: {
    pending_council: 'pending',
    active: 'success',
    expired: 'warning',
    // Revoked is history, deliberately kept. It is not a failure to fix — the
    // row is retained so the record of who held authority when survives.
    revoked: 'neutral',
    // Turned down by the municipality: the proposal needs changing before it
    // can go again.
    rejected: 'rejected',
  },
  /** A request between a municipality and its contractor. */
  serviceRequest: {
    // Waiting on the other side to pick it up.
    submitted: 'pending',
    // Being dealt with: nothing for the asker to do, nothing wrong.
    accepted: 'neutral',
    in_progress: 'neutral',
    settled: 'success',
    declined: 'rejected',
  },
  /** A contractor company. */
  company: {
    active: 'success',
    suspended: 'warning',
    closed: 'neutral',
  },
  /** Anything that is written, reviewed and then published. */
  publication: {
    draft: 'neutral',
    in_review: 'pending',
    approved: 'approved',
    scheduled: 'warning',
    published: 'success',
  },
  /** A seat on a training session. */
  enrollment: {
    pending: 'pending',
    confirmed: 'success',
    waitlisted: 'neutral',
    cancelled: 'rejected',
  },
  /** Whether a seat was paid for. */
  payment: {
    unpaid: 'warning',
    invoiced: 'pending',
    organization: 'pending',
    paid: 'success',
    waived: 'neutral',
  },
  /** Whether someone turned up. */
  attendance: {
    unknown: 'neutral',
    attended: 'success',
    absent: 'rejected',
  },
  /** A bulletin mailing list membership. */
  subscriber: {
    pending: 'pending',
    subscribed: 'success',
    unsubscribed: 'neutral',
  },
  /** An inbound contact request. */
  request: {
    new: 'warning',
    in_progress: 'pending',
    done: 'success',
  },
  /**
   * A property owner's request for a copy of a document on their file.
   *
   * Separate from `request` above, which is a support queue with an entirely
   * different state set. Merging the two would give one word ("done", "sent")
   * two meanings depending on the screen — the exact drift this table exists to
   * prevent.
   *
   * `refused` is `rejected` rather than `warning`: it is an answer, arrived at
   * by a decision, and the owner is shown the reason alongside it. Drawing it
   * as a warning would suggest something still to be resolved.
   */
  documentRequest: {
    requested: 'pending',
    sent: 'success',
    refused: 'rejected',
  },
  /** A prosecution's progress through the court. */
  docket: {
    draft: 'neutral',
    filed: 'pending',
    resolved: 'success',
    returned: 'warning',
  },
  /** An account, a by-law, a category — anything that is simply on or off. */
  enabled: {
    active: 'success',
    enabled: 'success',
    inactive: 'neutral',
    disabled: 'neutral',
  },
  /**
   * An invoice's life. `open` is a warning rather than pending: money is owed
   * and someone has to do something, which is not the same as waiting on a
   * queue. `uncollectible` is the decision to stop chasing, so it reads as
   * ended — not as an error still in play.
   */
  invoice: {
    draft: 'neutral',
    open: 'warning',
    paid: 'success',
    void: 'neutral',
    uncollectible: 'rejected',
  },
  /**
   * A subscription's life. `past_due` is the red rather than the amber the
   * other vocabularies give it: for a subscription this is the state where
   * people lose access to the portal, and it must not read like an expiry
   * anyone can leave until next week.
   */
  subscription: {
    draft: 'neutral',
    trialing: 'pending',
    active: 'success',
    past_due: 'rejected',
    unpaid: 'rejected',
    paused: 'warning',
    canceled: 'neutral',
  },
  /**
   * A permit or licence application. `info_requested` is the amber: the
   * applicant has something to do. Withdrawn is the applicant's own choice, so
   * it reads as ended without being a refusal.
   */
  permitApplication: {
    draft: 'neutral',
    submitted: 'pending',
    under_review: 'pending',
    info_requested: 'warning',
    approved: 'success',
    refused: 'rejected',
    withdrawn: 'neutral',
  },
  /** An issued permit. */
  permit: {
    active: 'success',
    expired: 'warning',
    revoked: 'rejected',
  },
  /** An application fee. */
  permitFee: {
    not_required: 'neutral',
    due: 'warning',
    paid: 'success',
    waived: 'neutral',
  },
  /** A checklist inspection. */
  inspection: {
    scheduled: 'pending',
    in_progress: 'pending',
    completed: 'success',
    cancelled: 'neutral',
  },
  /** How an inspection, or one item on it, came out. */
  inspectionResult: {
    pass: 'success',
    fail: 'rejected',
    na: 'neutral',
  },
  /** A background job. */
  run: {
    queued: 'pending',
    running: 'pending',
    succeeded: 'success',
    failed: 'rejected',
  },
  /**
   * A by-law ticket. Issued and served are waits on the owner; disputed and
   * overdue are still owed but going wrong; collections has left the
   * municipality's hands; void was cancelled by a decision that is not a
   * failure, so it reads neutral like other history.
   */
  ticket: {
    issued: 'pending',
    served: 'pending',
    paid: 'success',
    disputed: 'warning',
    overdue: 'warning',
    collections: 'rejected',
    void: 'neutral',
  },
} as const satisfies Record<string, Record<string, StatusKind>>;

export type StatusVocabulary = keyof typeof statusTone;
